import { useEffect, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Phone } from "lucide-react";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import StatsBar from "./components/StatsBar";
import WhyUs from "./components/WhyUs";
import Brands from "./components/Brands";
import Products from "./components/Products";
import LabTable from "./components/LabTable";
import Serve from "./components/Serve";
import HowToOrder from "./components/HowToOrder";
import Contact from "./components/Contact";
import SeoPage from "./components/SeoPage";
import { LangProvider } from "./lib/i18n";
import { PHONE_TEL } from "./lib/data";

function IntroOverlay() {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink"
      exit={{ y: "-100%" }}
      transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
    >
      <motion.img
        src="/assets/gt-logo.png"
        alt="Ghauri Traders"
        className="w-40 h-40 sm:w-52 sm:h-52 rounded-[2rem] object-cover shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
        initial={{ opacity: 0, scale: 0.75, filter: "blur(8px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
      />
    </motion.div>
  );
}

function HomePage() {
  return (
    <main>
      <Hero />
      <Marquee />
      <StatsBar />
      <section id="about" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-20" data-testid="about-section">
        <WhyUs />
        <Brands />
      </section>
      <section id="products" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 scroll-mt-20" data-testid="products-section">
        <Products />
        <LabTable />
        <Serve />
        <HowToOrder />
      </section>
      <Contact />
    </main>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  const rm = useReducedMotion();
  const [intro, setIntro] = useState(!rm);

  useEffect(() => {
    if (rm) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const timer = setTimeout(() => setIntro(false), 2000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      lenis.destroy();
      window.__lenis = null;
    };
  }, [rm]);

  return (
    <LangProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="bg-ink text-cream font-body antialiased">
          <AnimatePresence>{intro && <IntroOverlay />}</AnimatePresence>
          <div className="grain" aria-hidden="true" />
          <a
            href={PHONE_TEL}
            aria-label="Call Ghauri Traders"
            data-testid="sticky-call-button"
            className="md:hidden fixed bottom-5 end-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brass text-ink shadow-[0_10px_35px_rgba(0,0,0,0.5)] pulse-ring active:scale-90 transition-transform"
          >
            <Phone size={24} strokeWidth={2.2} />
          </a>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Nav />
                  <HomePage />
                </>
              }
            />
            <Route path="/lucky-cement-price" element={<SeoPage page="lucky" />} />
            <Route path="/bestway-cement-islamabad" element={<SeoPage page="bestway" />} />
          </Routes>
        </div>
      </BrowserRouter>
    </LangProvider>
  );
}
